import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiException } from '@/lib/api/types';
import type { Menu } from '@/features/auth/types';
import { AUTH_QUERY_KEY } from '@/features/auth/useAuth';
import * as adminApi from './api';
import type {
  ActiveSession,
  AiUsageReport,
  BatchOverview,
  MemberSummary,
} from './types';

/**
 * 관리자 화면 훅.
 *
 * 변경 후 무엇을 다시 읽어야 하는지가 이 파일의 핵심이다.
 * 권한을 바꿨는데 화면이 옛 상태를 보여주면 관리자는 저장이 안 된 줄 알고 또 누른다.
 */

const MEMBERS_KEY = ['admin', 'members'] as const;
const MENUS_KEY = ['admin', 'menus'] as const;
const SESSIONS_KEY = ['admin', 'sessions'] as const;

/** 권한 매트릭스의 열 머리글. 메뉴는 배포하지 않는 한 늘지 않는다. */
export function useAdminMenus() {
  return useQuery<Menu[], ApiException>({
    queryKey: MENUS_KEY,
    queryFn: adminApi.fetchMenus,
    staleTime: Infinity,
  });
}

export function useMembers() {
  return useQuery<MemberSummary[], ApiException>({
    queryKey: MEMBERS_KEY,
    queryFn: adminApi.fetchMembers,
  });
}

/**
 * 활성 세션.
 *
 * 자동 갱신하지 않는다. 세션 목록은 관리자가 무언가를 하려고 열어보는 화면이지
 * 계속 지켜보는 화면이 아니다. 주기적으로 폴링하면 홈서버 Redis 에 의미 없는 부하만 준다.
 */
export function useSessions() {
  return useQuery<ActiveSession[], ApiException>({
    queryKey: SESSIONS_KEY,
    queryFn: adminApi.fetchSessions,
    staleTime: 10_000,
  });
}

export function useAiUsage(month: string) {
  return useQuery<AiUsageReport, ApiException>({
    queryKey: ['admin', 'ai-usage', month],
    queryFn: () => adminApi.fetchAiUsage(month),
  });
}

/**
 * 회원 목록을 다시 읽는다.
 *
 * 세션 목록도 함께 무효화하는 이유
 *   비활성화·강등·비밀번호 재설정은 세션을 함께 끊는다. 세션 목록을
 *   그대로 두면 이미 사라진 세션이 화면에 남아 있고, 종료를 눌러도 아무 일도
 *   일어나지 않는 것처럼 보인다.
 */
function useMemberMutation<TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();

  return useMutation<TData, ApiException, TVariables>({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEMBERS_KEY });
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });

      // 🔴 자기 자신의 권한을 바꿨을 수 있다
      //   /api/auth/me 를 다시 읽지 않으면 상단 메뉴가 옛 상태로 남는다.
      //   관리자는 메뉴 권한 검사를 건너뛰므로 실제로 막히지는 않지만,
      //   화면과 실제가 어긋나 있는 상태 자체가 나중에 사람을 헷갈리게 한다.
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });
}

export function useCreateMember() {
  return useMemberMutation(adminApi.createMember);
}

export function useUpdateMember() {
  return useMemberMutation(
    (variables: { id: number; request: Parameters<typeof adminApi.updateMember>[1] }) =>
      adminApi.updateMember(variables.id, variables.request),
  );
}

export function useUpdateMenus() {
  return useMemberMutation((variables: { id: number; menuCodes: string[] }) =>
    adminApi.updateMenus(variables.id, variables.menuCodes),
  );
}

export function useResetPassword() {
  return useMemberMutation((variables: { id: number; password: string }) =>
    adminApi.resetPassword(variables.id, variables.password),
  );
}

export function useUnlockMember() {
  return useMemberMutation((id: number) => adminApi.unlockMember(id));
}

export function useTerminateSession() {
  return useMemberMutation((sessionId: string) => adminApi.terminateSession(sessionId));
}

export function useTerminateMemberSessions() {
  return useMemberMutation((id: number) => adminApi.terminateMemberSessions(id));
}

/**
 * 배치 실행 이력. (V51)
 *
 * 30초마다 다시 읽는다. 배치는 하루 몇 번뿐이지만, 이 화면을 여는 이유가
 * 대개 "지금 도는 배치를 지켜보는 것" 이다. 8/18 자동 종목 선별을 볼 때처럼.
 * 그때 새로고침을 누르게 하지 않는다.
 *
 * 탭이 뒤에 있으면 멈춘다. TanStack Query 기본 동작이다.
 */
export function useBatches(job: string | null, page: number) {
  return useQuery<BatchOverview, ApiException>({
    queryKey: ['admin', 'batches', job, page],
    queryFn: () => adminApi.fetchBatches(job, page),
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    // 페이지를 넘길 때 목록이 사라졌다 나타나지 않게 한다.
    placeholderData: (previous) => previous,
  });
}
